import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { ZhhSessionLimitAction, ZhhSessionLimitSelector } from "@obg/ngx.common";
import { AuthSelector, CoreState, Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, EMPTY, Observable, of } from "rxjs";
import { filter, pairwise, switchMap } from "rxjs/operators";

@Injectable({
	providedIn: "root",
})
export class ZhhSessionLimitGuardService {
	private afterLogin$ = this.store.select(AuthSelector.isAuthenticated).pipe(
		pairwise(),
		filter(([prev, next]) => !prev && next)
	);

	constructor(
		private store: Store<CoreState>,
		private sessionLimitAction: ZhhSessionLimitAction,
		private sessionLimitSelector: ZhhSessionLimitSelector
	) {}

	initialize$() {
		return this.afterLogin$.pipe(
			switchMap(() => this.store.pipe(select(JurisdictionSelector.get), ignoreNil())),
			filter((jurisdiction) => jurisdiction === Jurisdiction.Zhh),
			switchMap(() => this.store.select(RouteSelector.getCurrent)),
			switchMap((currentRoute) => {
				if (this.isBlockedRoute(currentRoute?.name)) {
					return this.isAccessAllowed$();
				}
				return EMPTY;
			})
		);
	}

	isAccessAllowed$(): Observable<boolean> {
		this.store.dispatch(this.sessionLimitAction.get());

		return combineLatest([
			this.store.select(AuthSelector.isAuthenticated),
			this.store.pipe(select(this.sessionLimitSelector.getZhhSessionLimit())),
		]).pipe(
			filter(([, state]) => !!state?.sessionLimit),
			switchMap(([isAuth, sessionLimitState]) => {
				if (!isAuth) {
					return of(true);
				}
				return of(!(sessionLimitState?.sessionLimit && sessionLimitState?.sessionLimit?.blocked));
			})
		);
	}

	private isBlockedRoute(routeName?: string): boolean {
		return (
			!!routeName &&
			["casino.game-launcher"].some((route) => routeName.toLowerCase().includes(route.toLowerCase()))
		);
	}
}
